import React from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, Radio, RadioGroup, Typography
} from '@mui/material';
import { daysLabel } from '../format';
import { DocumentCard, Export1cSide } from '../types';
import { BORDER } from './cardKit';

export type ConfirmKind = 'retry' | 'extend' | 'hide' | 'unhide' | 'reject' | 'delete' | 'export1c';

const SIDE_LABELS: Record<Export1cSide, string> = {
  receiver: 'Как получатель (приход)',
  sender: 'Как отправитель (расход)'
};

const CONFIRM_TITLES: Record<ConfirmKind, string> = {
  retry: 'Повторная обработка',
  extend: 'Продление срока',
  hide: 'Скрыть документ',
  unhide: 'Вернуть документ',
  reject: 'Отклонить документ',
  delete: 'Удалить черновик',
  export1c: 'Выгрузка накладной в 1С'
};

interface Props {
  confirm: ConfirmKind | null;
  doc: DocumentCard | null;
  busy: boolean;
  side: Export1cSide;
  onSide: (side: Export1cSide) => void;
  deleteAck: boolean;
  onDeleteAck: (value: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Подтверждение действия над документом: что произойдёт, выбор стороны для 1С, согласие на удаление черновика */
const CardConfirmDialog: React.FC<Props> = ({ confirm, doc, busy, side, onSide, deleteAck, onDeleteAck, onCancel, onConfirm }) => (
  <Dialog open={confirm !== null} onClose={() => !busy && onCancel()} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, fontSize: 16, fontWeight: 700 }}>
      {confirm ? CONFIRM_TITLES[confirm] : ''}
    </DialogTitle>
    <DialogContent sx={{ pt: '16px !important' }}>
      {confirm === 'retry' && doc && (
        <Typography variant="body2">
          Документ №{doc.id} вернётся в статус «{doc.retryStatusName}» и будет обработан заново. Продолжить?
        </Typography>
      )}
      {confirm === 'extend' && doc && (
        <Typography variant="body2">
          Срок документа №{doc.id} будет продлён: счётчик простоя обнулится
          {doc.watch ? ` (сейчас без изменений ${daysLabel(doc.watch.idleDays)})` : ''}. Продолжить?
        </Typography>
      )}
      {confirm === 'hide' && doc && (
        <Typography variant="body2">
          Документ №{doc.id} будет скрыт от аптек. Запись попадёт в историю статусов, документ можно вернуть в любой момент. Продолжить?
        </Typography>
      )}
      {confirm === 'unhide' && doc && (
        <Typography variant="body2">
          Документ №{doc.id} снова станет виден аптекам. Счётчик простоя обнулится, чтобы автоочистка не скрыла его повторно. Продолжить?
        </Typography>
      )}
      {confirm === 'reject' && doc && (
        <Typography variant="body2">
          Документ №{doc.id} перейдёт из статуса «{doc.statusName}» в «Отклонен». Продолжить?
        </Typography>
      )}
      {confirm === 'delete' && doc && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Alert severity="warning" sx={{ py: 0 }}>Действие необратимо: черновик №{doc.id} будет удалён вместе с позициями, кодами маркировки и коробами.</Alert>
          <FormControlLabel
            control={<Checkbox size="small" checked={deleteAck} onChange={e => onDeleteAck(e.target.checked)} />}
            label={<Typography variant="body2">Я понимаю, что восстановить документ будет нельзя</Typography>}
          />
        </Box>
      )}
      {confirm === 'export1c' && doc && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2">Накладная по документу №{doc.id} будет отправлена в 1С.</Typography>
          {doc.export1cSides.length > 1 && (
            <RadioGroup value={side} onChange={e => onSide(e.target.value as Export1cSide)}>
              {doc.export1cSides.map(s => (
                <FormControlLabel key={s} value={s} control={<Radio size="small" />} label={<Typography variant="body2">{SIDE_LABELS[s]}</Typography>} />
              ))}
            </RadioGroup>
          )}
          {doc.export1cSides.length === 1 && (
            <Typography variant="body2" color="text.secondary">{SIDE_LABELS[doc.export1cSides[0]]}</Typography>
          )}
        </Box>
      )}
    </DialogContent>
    <Divider />
    <DialogActions sx={{ px: 3, py: 1.5 }}>
      <Button color="inherit" onClick={onCancel} disabled={busy}>Отмена</Button>
      <Button
        variant="contained" disableElevation onClick={onConfirm}
        color={confirm === 'delete' ? 'error' : 'primary'}
        disabled={busy || (confirm === 'delete' && !deleteAck)}
        startIcon={busy ? <CircularProgress size={16} /> : undefined}
      >
        {confirm === 'delete' ? 'Удалить' : 'Подтвердить'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default React.memo(CardConfirmDialog);
